import { Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import { useAccessiblePayerCustomerIds } from '@folio/hooks/useAccessiblePayerCustomerIds';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  BranchDto,
  customerControllerGetCustomerQueryOptions,
  customerControllerGetInvoicingScheduleQueryOptions,
  Period,
} from 'korint-sdk';
import { formatDate, formatPrice } from '../../lib';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type InvoicesScheduleByCustomerModuleProps = {
  customerId: string;
  workingBranch?: BranchDto;
  showCustomerId?: boolean;
};

const formatPeriod = ({ startedAt, endedAt }: Period) =>
  `${formatDate(new Date(startedAt))} – ${formatDate(new Date(endedAt))}`;

export function InvoicesScheduleModule() {
  const { policy, workingBranch } = usePolicyOnDashboard();
  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  return (
    <Stack>
      {payerCustomerIds.map(customerId => (
        <InvoicesScheduleByCustomerModule
          key={customerId}
          customerId={customerId}
          workingBranch={workingBranch}
          showCustomerId={payerCustomerIds.length > 1}
        />
      ))}
    </Stack>
  );
}

function InvoicesScheduleByCustomerModule({
  customerId,
  workingBranch,
  showCustomerId = false,
}: InvoicesScheduleByCustomerModuleProps) {
  const customerInformations = usePolicyProductConfig('customerInformations');

  const { t } = useTranslation();

  const { data: customer } = useSuspenseQuery({
    ...customerControllerGetCustomerQueryOptions(customerId, {
      branchId: workingBranch?.id,
    }),
  });

  const { data: invoicingSchedule } = useSuspenseQuery({
    ...customerControllerGetInvoicingScheduleQueryOptions(customerId, {
      branchId: workingBranch?.id,
    }),
    select: d => d.data,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('invoicesSchedule.module.title')}
          {showCustomerId && (
            <Text inline size="sm" weight="normal" tone="muted" className="ml-2">
              (
              {customerDisplayName(
                customer ? [customer] : [],
                customerInformations?.fieldsConfig,
              ) || customerId}
              )
            </Text>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {invoicingSchedule.length === 0 ? (
          <div className="py-4 text-center text-muted-foreground">
            {t('invoicesSchedule.module.noSchedule')}
          </div>
        ) : (
          <ul className="flex flex-col" data-testid="invoicing-schedule-list">
            {invoicingSchedule.map((invoice, index) => {
              const isLast = index === invoicingSchedule.length - 1;

              return (
                <li key={invoice.issuedAt} className="group flex gap-3">
                  {/* Timeline */}
                  <Stack align="center" gap={0}>
                    <div className="relative mt-3.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-primary/30 bg-background transition-all duration-200 group-hover:border-primary">
                      <div className="h-1.5 w-1.5 rounded-full bg-transparent transition-all duration-200 group-hover:bg-primary" />
                    </div>
                    {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
                  </Stack>

                  {/* Card */}
                  <Group
                    justify="between"
                    gap={0}
                    className={`flex-1 rounded-xl bg-muted/50 px-4 py-3 transition-colors duration-200 group-hover:bg-primary/10 ${!isLast ? 'mb-2' : ''}`}
                  >
                    <Stack gap={0} className="gap-0.5">
                      <Text inline size="sm" weight="medium">
                        {formatDate(new Date(invoice.issuedAt))}
                      </Text>
                      <Text inline size="xs" tone="muted">
                        {t('invoicesSchedule.module.period')} : {formatPeriod(invoice.period)}
                      </Text>
                    </Stack>
                    <Text inline size="sm" weight="bold">
                      {formatPrice(invoice.total.amountWithFeesAndTaxes)}
                    </Text>
                  </Group>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
